import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, ClipboardList, Phone, CalendarDays, MessageSquare, ChevronRight, Home, Building2, MapPin } from 'lucide-react';
import { FaAirbnb } from 'react-icons/fa6';
import { Button } from '../components/ui/button';
import { trackQuoteConversion } from '../utils/analytics';

const ASSETS = {
  allSet: '/brand/youre-all-set.png',
  guru: '/brand/guru-pointing.png',
  van: '/brand/guru-van-bg.png',
  logo: 'https://customer-assets.emergentagent.com/job_09ef4963-c96f-46c6-a08d-052afb978ee9/artifacts/j66rn420_Gleam%20Guru%20Cleaning%20%5BLogo%20Design%5D%20No%20Gleam.png',
};
const PHONE_HREF = 'tel:9717150335';
export const CONFIRM_FLAG = 'gg_quote_confirmed';

const HEAD = { fontFamily: 'Outfit, sans-serif' };
const BODY = { fontFamily: 'Manrope, sans-serif' };

const STEPS = [
  { n: '01', Icon: ClipboardList, title: 'We Review Your Request', desc: "We'll look over your space, service needs, and details." },
  { n: '02', Icon: Phone, title: 'A Gleam Guru Reaches Out', desc: "We'll contact you to discuss your clean and answer any questions." },
  { n: '03', Icon: CalendarDays, title: 'We Get Your Clean Scheduled', desc: "Once everything looks good, we'll lock in your service date." },
];

const fade = { hidden: { opacity: 0, y: 18 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } } };
const stagger = { visible: { transition: { staggerChildren: 0.12 } } };

const Grain = () => (
  <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.12] mix-blend-overlay"
    style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")" }} />
);

const StepCard = ({ n, Icon, title, desc }) => (
  <motion.div variants={fade} className="rounded-2xl border-2 border-[#F56F05]/60 bg-zinc-950/70 p-6 sm:p-7 flex flex-col" data-testid={`next-step-${n}`}>
    <div className="flex items-start justify-between mb-5">
      <span className="gg-display text-5xl sm:text-6xl text-[#F56F05]">{n}</span>
      <Icon className="w-10 h-10 sm:w-11 sm:h-11 text-[#F56F05]" strokeWidth={2.2} aria-hidden />
    </div>
    <h3 className="gg-display text-xl sm:text-2xl text-white mb-2">{title}</h3>
    <p className="text-sm sm:text-[15px] text-zinc-300 leading-relaxed" style={BODY}>{desc}</p>
  </motion.div>
);

const ServiceTag = ({ Icon, label }) => (
  <div className="flex flex-col items-center gap-2 text-center min-w-[88px]">
    <Icon className="w-9 h-9 sm:w-11 sm:h-11 text-[#F56F05]" strokeWidth={2} aria-hidden />
    <span className="text-[11px] sm:text-xs font-extrabold text-white uppercase tracking-wider" style={HEAD}>{label}</span>
  </div>
);

export default function QuoteConfirmationPage() {
  const location = useLocation();
  const viaSubmit = location.state?.confirmed === true;
  const stored = (() => { try { return JSON.parse(sessionStorage.getItem(CONFIRM_FLAG) || 'null'); } catch { return null; } })();
  const details = viaSubmit ? location.state : stored;
  const firstName = (details?.first_name || '').trim();
  const service = (details?.service || '').trim();

  useEffect(() => {
    if (viaSubmit) {
      const { submission_id, first_name, service: svc } = location.state;
      sessionStorage.setItem(CONFIRM_FLAG, JSON.stringify({ confirmed: true, submission_id, first_name, service: svc }));
      const firedKey = `gg_conv_${submission_id || 'na'}`;
      if (!sessionStorage.getItem(firedKey)) {
        trackQuoteConversion({ submissionId: submission_id, service: svc });
        sessionStorage.setItem(firedKey, '1');
      }
    }
    window.scrollTo({ top: 0 });
  }, [viaSubmit, location.state]);

  if (!viaSubmit && !details?.confirmed) return <Navigate to="/#quote-form" replace />;

  return (
    <div className="bg-black text-white relative overflow-x-hidden" data-testid="quote-confirmation-page" style={BODY}>
      <Grain />

      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_35%,rgba(245,111,5,0.28),transparent_55%)]" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 pt-32 sm:pt-36 lg:pt-40 pb-6 lg:pb-0">
          <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 items-start">
            {/* Copy */}
            <motion.div initial="hidden" animate="visible" variants={stagger} className="lg:col-span-7 relative z-10">
              <motion.p variants={fade} className="flex items-center gap-3 text-[#F56F05] font-bold tracking-[0.35em] uppercase text-xs sm:text-sm" data-testid="request-received-label">
                <CheckCircle2 className="w-8 h-8 sm:w-10 sm:h-10" strokeWidth={1.6} aria-hidden /> Request Received
              </motion.p>
              <motion.img variants={fade} src={ASSETS.allSet} alt="You're all set." data-testid="all-set-graphic"
                className="w-full max-w-[560px] lg:max-w-[600px] mt-4 sm:mt-6 -ml-1 drop-shadow-[0_25px_45px_rgba(0,0,0,0.6)]" />
              <motion.h1 variants={fade} className="mt-6 sm:mt-8 text-2xl sm:text-3xl font-bold text-white leading-snug max-w-xl" style={HEAD} data-testid="confirmation-headline">
                {firstName ? <><span className="text-[#F56F05]">{firstName}</span>, your{service ? <> <span className="text-[#F56F05]">{service}</span></> : ''} request is officially in the hands of a Gleam Guru.</>
                  : service ? <>Your <span className="text-[#F56F05]">{service}</span> request is officially in the hands of a Gleam Guru.</>
                    : 'Your request is officially in the hands of a Gleam Guru.'}
              </motion.h1>
              <motion.p variants={fade} className="mt-4 text-base sm:text-lg text-zinc-300 leading-relaxed max-w-xl" data-testid="confirmation-copy">
                Thanks for reaching out! We&apos;ve received your request and we&apos;re reviewing the details now. A member of our team will be in touch soon to discuss your clean and answer any questions.
              </motion.p>
            </motion.div>

            {/* Guru + logo */}
            <div className="lg:col-span-5 relative flex lg:block justify-center">
              <motion.img initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
                src={ASSETS.guru} alt="Gleam Guru pointing at you" data-testid="guru-character"
                className="w-[72%] max-w-[360px] sm:max-w-[420px] lg:max-w-none lg:w-full lg:-mr-6 lg:mt-2 drop-shadow-[0_30px_60px_rgba(0,0,0,0.7)] select-none" draggable={false} />
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
                className="absolute right-0 top-2 sm:top-4 lg:-right-2 lg:top-0 flex flex-col items-center text-center w-[28%] max-w-[150px] lg:max-w-[170px]" data-testid="confirmation-brand-lockup">
                <img src={ASSETS.logo} alt="Gleam Guru" className="w-full drop-shadow-[0_0_25px_rgba(245,111,5,0.45)]" />
                <p className="gg-display mt-2 text-white italic text-xs sm:text-base lg:text-lg">The Masters<br />of Clean.</p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT HAPPENS NEXT */}
      <section className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 pt-6 lg:pt-2 pb-8" data-testid="what-happens-next">
        <motion.h2 initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fade}
          className="gg-display text-4xl sm:text-5xl lg:text-6xl mb-6 sm:mb-8">
          What Happens <span className="text-[#F56F05]">Next?</span>
        </motion.h2>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-40px' }} variants={stagger} className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
          {STEPS.map(s => <StepCard key={s.n} {...s} />)}
        </motion.div>

        {/* NEED US SOONER */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fade}
          className="mt-4 sm:mt-5 rounded-2xl border-2 border-[#F56F05]/60 bg-zinc-950/70 p-5 sm:p-7 flex flex-col md:flex-row md:items-center gap-5 md:gap-8" data-testid="need-us-sooner">
          <div className="flex items-start gap-4 sm:gap-5 flex-1">
            <span className="w-14 h-14 sm:w-16 sm:h-16 rounded-full border-[3px] border-[#F56F05] flex items-center justify-center flex-shrink-0">
              <MessageSquare className="w-7 h-7 sm:w-8 sm:h-8 text-[#F56F05]" strokeWidth={2.2} aria-hidden />
            </span>
            <div>
              <h3 className="gg-display text-3xl sm:text-4xl">Need Us Sooner?</h3>
              <p className="mt-2 text-sm sm:text-base text-zinc-300 leading-relaxed">Have a question or want to talk now? We&apos;re here to help.</p>
            </div>
          </div>
          <Button asChild className="w-full md:w-auto bg-[#F56F05] hover:bg-[#C2410C] text-white font-bold text-base sm:text-lg h-14 sm:h-16 px-8 sm:px-10 rounded-full shadow-[0_15px_40px_-10px_rgba(245,111,5,0.6)] transition-all" data-testid="talk-with-guru-cta">
            <a href={PHONE_HREF} style={HEAD}>Talk With A Gleam Guru <ChevronRight className="ml-1 w-5 h-5" /></a>
          </Button>
        </motion.div>
      </section>

      {/* SERVICE INDICATORS + VAN */}
      <section className="relative" data-testid="van-section">
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 pt-6 pb-10 z-20">
          <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-6 sm:gap-x-10 gap-y-5" data-testid="service-indicators">
            <ServiceTag Icon={Home} label="Residential" />
            <span className="hidden sm:block w-px h-14 bg-[#F56F05]/70" aria-hidden />
            <ServiceTag Icon={Building2} label="Commercial" />
            <span className="hidden sm:block w-px h-14 bg-[#F56F05]/70" aria-hidden />
            <ServiceTag Icon={FaAirbnb} label="Airbnb" />
            <span className="hidden lg:block w-px h-14 bg-[#F56F05]/70 ml-2" aria-hidden />
            <div className="flex items-center gap-3 basis-full sm:basis-auto justify-center" data-testid="service-area-tag">
              <MapPin className="w-10 h-10 sm:w-12 sm:h-12 text-[#F56F05]" strokeWidth={2} aria-hidden />
              <p className="text-sm sm:text-base font-extrabold uppercase tracking-[0.2em] leading-relaxed" style={HEAD}>Portland, OR<br />Vancouver, WA</p>
            </div>
          </div>
        </div>
        <div className="relative w-full h-[260px] sm:h-[360px] lg:h-[480px] overflow-hidden">
          <div className="absolute inset-x-0 top-0 h-20 sm:h-28 bg-gradient-to-b from-black to-transparent z-10" aria-hidden />
          <img src={ASSETS.van} alt="Gleam Guru van overlooking Portland, Oregon" data-testid="van-artwork"
            className="w-full h-full object-cover object-[28%_65%] sm:object-[35%_60%] lg:object-center" />
        </div>
      </section>
    </div>
  );
}
